import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const shippingDestination = { zip: '', province: '' };

class JuShippingCalculator extends Component {
  requiredRefs = ['zip', 'province', 'calculateButton', 'results'];
  #request = null;
  #listeners = null;
  #hasResults = false;
  #cartVersion = 0;
  #cartBusy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#restoreDestination();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartChanged, options);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#cartChanged, options);
    document.addEventListener('ju:shipping-query', this.#otherQuery, options);
    window.addEventListener('pageshow', this.#invalidate, options);
  }

  disconnectedCallback() {
    this.#request?.abort();
    this.#listeners?.abort();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreDestination();
    this.#invalidate();
  }

  #cartChanged = async (event) => {
    const version = ++this.#cartVersion;
    this.#cartBusy = true;
    this.#invalidate();
    try { await event.promise; } catch (_) {}
    if (version !== this.#cartVersion || !this.isConnected) return;
    this.#cartBusy = false;
    this.#invalidate();
  };

  #otherQuery = (event) => {
    if (event.detail !== this) this.#invalidate();
  };

  #invalidate = () => {
    const wasActive = this.#request || this.#hasResults;
    this.#request?.abort();
    this.#request = null;
    this.#hasResults = false;
    this.refs.calculateButton.disabled = this.#cartBusy;
    this.refs.results.removeAttribute('aria-busy');
    if (wasActive) this.refs.results.textContent = this.dataset.changed;
  };

  #restoreDestination() {
    if (shippingDestination.zip) this.refs.zip.value = shippingDestination.zip;
    if (shippingDestination.province) this.refs.province.value = shippingDestination.province;
  }

  destinationChanged = () => {
    shippingDestination.zip = this.refs.zip.value;
    shippingDestination.province = this.refs.province.value;
    this.#invalidate();
  };

  calculate = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#cartBusy) return;
    if (!(event.target instanceof HTMLFormElement) || !event.target.reportValidity()) return;
    this.#request?.abort();
    document.dispatchEvent(new CustomEvent('ju:shipping-query', { detail: this }));
    const controller = new AbortController();
    this.#request = controller;
    this.#hasResults = false;
    const timeout = setTimeout(() => controller.abort(), 30000);
    shippingDestination.zip = this.refs.zip.value;
    shippingDestination.province = this.refs.province.value;
    const zip = this.refs.zip.value.replace(/\D/g, '');
    const province = this.refs.province.value;
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': province,
    });
    const root = window.Shopify.routes.root;
    this.refs.calculateButton.disabled = true;
    this.refs.results.setAttribute('aria-busy', 'true');
    this.refs.results.textContent = this.dataset.loading;
    try {
      const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!prepared.ok) throw new Error('Shipping request failed');
      let result = null;
      while (result === null) {
        await new Promise((resolve, reject) => {
          const onAbort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
          const timer = setTimeout(() => {
            controller.signal.removeEventListener('abort', onAbort);
            resolve();
          }, 1000);
          if (controller.signal.aborted) onAbort();
          else controller.signal.addEventListener('abort', onAbort, { once: true });
        });
        const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`, {
          credentials: 'same-origin', signal: controller.signal, cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Shipping result failed');
        result = await response.json();
      }
      if (this.#request !== controller || !this.isConnected) return;
      if (!Array.isArray(result.shipping_rates)) throw new Error('Invalid shipping response');
      this.#renderRates(result.shipping_rates);
      this.#hasResults = true;
    } catch (_) {
      if (this.#request === controller && this.isConnected) this.refs.results.textContent = this.dataset.error;
    } finally {
      clearTimeout(timeout);
      if (this.#request === controller) {
        this.#request = null;
        this.refs.calculateButton.disabled = this.#cartBusy;
        this.refs.results.removeAttribute('aria-busy');
      }
    }
  };

  #formatDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T12:00:00Z`);
    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return null;
    return new Intl.DateTimeFormat(document.documentElement.lang || 'pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
    }).format(date);
  }

  #deliveryText(rate) {
    if (Array.isArray(rate.delivery_range) && rate.delivery_range.length === 2) {
      const dates = rate.delivery_range.map((value) => this.#formatDate(value));
      if (dates.every(Boolean)) return `${this.dataset.estimate} ${dates[0] === dates[1] ? dates[0] : dates.join(' – ')}`;
    }
    const date = this.#formatDate(rate.delivery_date);
    if (date) return `${this.dataset.estimate} ${date}`;
    const hasProviderText = /\b\d+\s*(?:[-–a]\s*\d+\s*)?(?:dias?|days?)\b|\b\d{2}[/-]\d{2}(?:[/-]\d{4})?\b/i.test(`${rate.presentment_name || rate.name || ''} ${rate.description || ''}`);
    return hasProviderText ? '' : this.dataset.missingEta;
  }

  #renderRates(rates) {
    this.refs.results.replaceChildren();
    if (!rates.length) {
      this.refs.results.textContent = this.dataset.empty;
      return;
    }
    const list = document.createElement('ul');
    list.className = 'ju-shipping__rates';
    for (const rate of rates) {
      const item = document.createElement('li');
      item.className = 'ju-shipping__rate';
      const line = document.createElement('div');
      line.className = 'ju-shipping__rate-line';
      const name = document.createElement('span');
      name.textContent = rate.presentment_name || rate.name || '';
      const price = document.createElement('strong');
      price.className = 'ju-shipping__price';
      const value = typeof rate.price === 'number' || typeof rate.price === 'string' && /^\d+(\.\d+)?$/.test(rate.price) ? Number(rate.price) : NaN;
      if (Number.isFinite(value) && value >= 0) {
        price.textContent = value === 0 ? this.dataset.free : new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
          style: 'currency', currency: rate.currency || this.dataset.currency || 'BRL',
        }).format(value);
      } else price.textContent = this.dataset.priceUnavailable;
      line.append(name, price);
      item.append(line);
      if (typeof rate.description === 'string' && rate.description) {
        const description = document.createElement('p');
        description.className = 'ju-shipping__description';
        description.textContent = rate.description;
        item.append(description);
      }
      const deliveryText = this.#deliveryText(rate);
      if (deliveryText) {
        const eta = document.createElement('p');
        eta.className = 'ju-shipping__eta';
        eta.textContent = deliveryText;
        item.append(eta);
      }
      list.append(item);
    }
    this.refs.results.append(list);
  }
}

if (!customElements.get('ju-shipping-calculator')) customElements.define('ju-shipping-calculator', JuShippingCalculator);
